import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { ContentGridCard } from '../cards/ContentGridCard';
import { EmptyState } from '../feedback/EmptyState';
import type { ContentItem } from '../../types';

interface ContentGridProps {
  items: ContentItem[];
  searchQuery?: string;
  activeFilters?: string[];
  isLoading?: boolean;
  onItemTap: (item: ContentItem) => void;
  onItemAction?: (item: ContentItem) => void;
  actionIcon?: React.ReactNode;
  renderMeta?: (item: ContentItem) => React.ReactNode;
  renderTags?: (item: ContentItem) => React.ReactNode;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  columns?: 2 | 3 | 4;
}

export function ContentGrid({
  items,
  searchQuery = '',
  activeFilters = [],
  isLoading = false,
  onItemTap,
  onItemAction,
  actionIcon,
  renderMeta,
  renderTags,
  emptyIcon,
  emptyTitle = 'No items found',
  emptyDescription,
  columns = 3,
}: ContentGridProps) {
  // Filter items based on search query and active filters
  const filteredItems = useMemo(() => {
    let result = items;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.tags.some((tag) => tag.name.toLowerCase().includes(query))
      );
    }

    // Tag/filter filters
    if (activeFilters.length > 0) {
      result = result.filter((item) =>
        activeFilters.some((filter) =>
          item.tags.some((tag) => tag.name.toLowerCase() === filter.toLowerCase())
        )
      );
    }

    return result;
  }, [items, searchQuery, activeFilters]);

  const gridColsClass = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  }[columns];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <RefreshCw className="w-6 h-6 text-primary" />
        </motion.div>
      </div>
    );
  }

  if (filteredItems.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon || <span className="text-3xl">🔍</span>}
        title={emptyTitle}
        description={
          emptyDescription ||
          (searchQuery
            ? `No items match "${searchQuery}"`
            : 'Try adjusting your filters')
        }
      />
    );
  }

  return (
    <div className={`grid ${gridColsClass} gap-6`}>
      <AnimatePresence mode="popLayout">
        {filteredItems.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ delay: index * 0.05, duration: 0.2 }}
          >
            <ContentGridCard
              item={item}
              onTap={() => onItemTap(item)}
              onAction={onItemAction ? () => onItemAction(item) : undefined}
              actionIcon={actionIcon}
              renderMeta={renderMeta}
              renderTags={renderTags}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
